import * as React from "react";
import { Select } from "@base-ui/react/select";
import Radio from "@sproutsocial/seeds-react-radio";
import Checkbox from "@sproutsocial/seeds-react-checkbox";
import { Icon } from "@sproutsocial/seeds-react-icon";
import { cn, type WithStringClassName } from "./cn";

// ─── CSS-class components (see menu.css) ────────────────────────────────────

export const StyledItem = React.forwardRef<
  React.ElementRef<typeof Select.Item>,
  WithStringClassName<React.ComponentProps<typeof Select.Item>>
>(({ className, ...rest }, ref) => (
  <Select.Item
    ref={ref}
    className={cn("seeds-select-item", className)}
    {...rest}
  />
));
StyledItem.displayName = "StyledItem";

export const StyledItemIndicator = React.forwardRef<
  React.ElementRef<typeof Select.ItemIndicator>,
  WithStringClassName<React.ComponentProps<typeof Select.ItemIndicator>>
>(({ className, ...rest }, ref) => (
  <Select.ItemIndicator
    ref={ref}
    className={cn("seeds-select-item-indicator", className)}
    {...rest}
  />
));
StyledItemIndicator.displayName = "StyledItemIndicator";

export const StyledItemText = Select.ItemText;

// ─── Icons ────────────────────────────────────────────────────────────────────

function CheckIcon() {
  return <Icon name="check-outline" fixedWidth aria-hidden />;
}

// ─── Component ────────────────────────────────────────────────────────────────

interface SelectItemProps {
  value: string | null;
  label: string;
  disabled?: boolean;
  inputType?: "icon" | "radio" | "checkbox" | "none";
  renderItem?: () => React.ReactNode;
  hidden?: boolean;
}

export default function SelectItem({
  value,
  label,
  disabled,
  inputType = "icon",
  renderItem,
  hidden,
}: SelectItemProps) {
  return (
    <StyledItem
      value={value}
      disabled={disabled}
      style={hidden ? { display: "none" } : undefined}
      data-qa-menu-item={label}
    >
      {inputType === "radio" ? (
        <StyledItemIndicator
          keepMounted
          render={(_props, state) => (
            <Radio
              checked={state.selected}
              onChange={() => {}}
              tabIndex={-1}
              id={`radio-${value}`}
              name="select-item"
            />
          )}
        />
      ) : inputType === "checkbox" ? (
        <StyledItemIndicator
          keepMounted
          render={(_props, state) => (
            <Checkbox
              checked={state.selected}
              onChange={() => {}}
              tabIndex={-1}
              id={`checkbox-${value}`}
              name="select-item"
            />
          )}
        />
      ) : inputType === "icon" ? (
        <StyledItemIndicator keepMounted>
          <CheckIcon />
        </StyledItemIndicator>
      ) : null}
      <StyledItemText>{renderItem ? renderItem() : label}</StyledItemText>
    </StyledItem>
  );
}
